import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import moment from 'moment'
import { forwardRef, ReactNode } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Controller, FieldError } from 'react-hook-form'

import { motionFadeIn } from '@apps-common/config/motion-variants'

type InputProps = {
  control: any
  id: string
  name: string
  className: string
  containerClassName?: string
  placeholder: string
  label: string
  showLabel?: boolean
  errors?: FieldError
  info?: string
  required?: boolean
  loading?: boolean
  disabled?: boolean
  value?: string
  offset?: 'endOfDay' | 'startOfDay' | null
  showTimeSelect?: boolean
  excludeDates?: Date[]
  minDate?: Date
  maxDate?: Date
  disablePriorTimes?: boolean
  disabledPriorTimesHourOffset?: number
  dateFormat?: string
  children?: ReactNode
}

const CustomDateTimeInput = forwardRef<any>((props: any, ref) => (
  <button
    type='button'
    className={classNames(props.className, 'border-[1px] !border-secondary-content/20 w-full sm:w-auto')}
    onClick={props.onClick}
    ref={ref}
  >
    {!!props.value && props.value}
    {!props.value && <span className='opacity-30'>{props.placeholder}</span>}
  </button>
))

export const DatePickerInputSm = (props: InputProps) => {
  const {
    control,
    id,
    name,
    className,
    containerClassName = '',
    placeholder,
    label,
    showLabel = false,
    errors,
    info,
    required = false,
    loading = false,
    disabled = false,
    value,
    offset,
    showTimeSelect = false,
    excludeDates = [],
    minDate,
    maxDate,
    disablePriorTimes = false,
    disabledPriorTimesHourOffset = 0,
    dateFormat = 'dd/MM/yyyy',
    children
  } = props

  const filterPassedTime = (time: Date) => {
    let currentDate = new Date()

    if (disabledPriorTimesHourOffset > 0) {
      currentDate = new Date(new Date().setHours(currentDate.getHours() + disabledPriorTimesHourOffset))
    }

    const selectedDate = new Date(time)

    return currentDate.getTime() < selectedDate.getTime()
  }

  return (
    <AnimatePresence mode='wait'>
      {loading && (
        <motion.div
          key={`${id}-input-sm-datepicker-skeleton`}
          {...motionFadeIn(0.2)}
          className={classNames('mb-2 animate-pulse', containerClassName)}
        >
          <div className={classNames('block ml-2 mb-2 h-[16px] bg-skeleton max-w-[20%] rounded-lg', { 'sr-only': !showLabel })} />
          <div className='relative'>
            <div className={classNames('px-4 py-2 rounded-3xl bg-skeleton w-full h-[36px]', className)} />
          </div>
          <p className='pl-2 pt-2 h-[5px]' />
        </motion.div>
      )}
      {!loading && (
        <motion.div key={`${id}-input-sm-datepicker`} {...motionFadeIn(0.2)} className={classNames('mb-2', containerClassName)}>
          <label className={classNames('block pl-2 pb-2 text-sm text-text font-bold', { 'sr-only': !showLabel })} htmlFor={id}>
            {label}
          </label>
          <div className='relative'>
            <Controller
              control={control}
              name={name}
              rules={{ required: required ? 'Required' : false }}
              render={({ field }) => (
                <DatePicker
                  className={classNames(className, 'border border-border')}
                  id={id}
                  name={name}
                  calendarClassName='block'
                  placeholderText={placeholder}
                  selected={value || field.value ? new Date(value || field.value) : null}
                  dateFormat={dateFormat}
                  onChange={(date) => {
                    let d = date?.toISOString()

                    if (offset === 'startOfDay') {
                      d = moment(date?.toISOString()).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toISOString()
                    } else if (offset === 'endOfDay') {
                      d = moment(date?.toISOString()).set({ hour: 23, minute: 59, second: 59, millisecond: 999 }).toISOString()
                    } else {
                      d = date?.toISOString()
                    }
                    field.onChange(d)
                  }}
                  withPortal
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  showTimeSelect={showTimeSelect}
                  excludeDates={excludeDates}
                  minDate={minDate}
                  maxDate={maxDate}
                  filterTime={disablePriorTimes ? filterPassedTime : undefined}
                  dropdownMode='select'
                  closeOnScroll
                  disabledKeyboardNavigation
                  customInput={<CustomDateTimeInput />}
                />
              )}
            />
            {children}
          </div>
          <p
            className={classNames(
              'pl-2 pt-2 text-[11px]',
              info && !errors?.message && 'text-secondary-content/60',
              (!info || errors?.message) && 'text-rose-600'
            )}
          >
            {errors?.message || info}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
